<script lang="ts" setup>
  import { useLocale } from '@tundr/i18n';
  import { rowCenterClass } from '@tundr/theme';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { isObject } from '@vueuse/core';
  import { computed, onMounted, ref, useTemplateRef, watchEffect } from 'vue';
  import VueMultiselect from 'vue-multiselect';
  import FontIcon from '../../../FontIcon/FontIcon.vue';
  import { InputWidthVariant } from '../../InputWrapper';
  import {
    errorClass,
    inputHeaderClass,
    inputLabelClass,
    inputWidthClass,
    inputWrapperClass,
  } from '../../InputWrapper/InputWrapper.css.ts';
  import './Select-override.css.ts';
  import { customCaretClass, selectInputWrapperClass } from './Select.css';
  import type { SelectModelValue, SelectProps } from './Select.types.ts';

  const { t } = useLocale('ui-kit');

  const props = withDefaults(defineProps<SelectProps>(), {
    searchable: true,
    clearOnSelect: true,
    closeOnSelect: true,
    showLabels: false,
    showNoOptions: true,
    resettable: false,
    showNoResults: true,
    allowEmpty: false,
    taggable: false,
    disabled: false,
    useTeleport: false,
    meta: undefined,
    internalSearch: true,
    width: InputWidthVariant.AUTO,
  });

  const elClass = computed(() => ['custom-select', props.class]);

  const selectedElRef = useTemplateRef<
    HTMLInputElement & { isOpen: boolean; toggle?: () => void }
  >('selectedElRef');

  const invalidTags = ref<Set<string>>(new Set());

  const getTagKey = (
    option: Record<string, unknown> | string | number,
  ): string => {
    if (typeof option === 'object') return String(option.label ?? '');
    return String(option);
  };

  const splitByComma = (value: string): string[] => {
    return value.split(',').map((item) => item.trim());
  };

  const emit = defineEmits<{
    blur: [ev?: Event, shouldValidate?: boolean];
    addTag: [value: string];
    search: [value: SelectProps['modelValue']];
    reset: [];
    'update:modelValue': [value: SelectProps['modelValue']];
    select: [value: SelectProps['modelValue']];
  }>();

  const handleChange = (value: SelectProps['modelValue']) => {
    // Clean up invalid tags when the value changes (e.g., tags are removed)
    if (props.taggable && Array.isArray(value)) {
      const currentTags = value.map(
        (item: Record<string, unknown> | string | number) => getTagKey(item),
      );
      invalidTags.value.forEach((tag: string) => {
        if (!currentTags.includes(tag)) {
          invalidTags.value.delete(tag);
        }
      });
    }
    emit('update:modelValue', value);
  };
  const handleBlur = (ev?: Event) => {
    emit('blur', ev);
  };

  const validateTag = (tag: string): boolean => {
    if (!props.tagValidationRegex) return true;
    const regex = new RegExp(props.tagValidationRegex);
    return regex.test(tag);
  };

  const handleTag = (newTag: string) => {
    const tags = splitByComma(newTag);

    const currentValue =
      (props.modelValue as { label: string; value: string }[]) || [];
    const newTags: { label: string; value: string }[] = [];

    tags.forEach((tag) => {
      const isValid = validateTag(tag);
      if (!isValid) {
        invalidTags.value.add(tag);
      } else {
        invalidTags.value.delete(tag);
      }

      // Only add if not already present
      const exists = currentValue.some(
        (item) => (typeof item === 'object' ? item.label : item) === tag,
      );
      if (!exists) {
        newTags.push({ label: tag, value: tag });
        emit('addTag', tag);
      }
    });

    if (newTags.length > 0) {
      const updatedValue = [...currentValue, ...newTags];
      emit('update:modelValue', updatedValue);
    }
  };

  const handleSearch = (searchValue: string) => {
    emit('search', searchValue);
  };

  const handleSelect = (selectedOption: SelectProps['modelValue']) => {
    emit('select', selectedOption);
    // fix to close the dropdown
    selectedElRef.value!.isOpen = false;
    emit('blur');
  };

  defineOptions({
    inheritAttrs: false,
  });

  const showError = computed<boolean>(() => {
    if (props.meta) {
      return !props.meta.valid && props.meta.touched;
    }
    return !!props.error;
  });

  const handleReset = () => {
    if (props.modelValue) {
      emit('update:modelValue', undefined);
      emit('reset');
    }
  };

  const selectClasses = computed(() => [
    'custom-select',
    inputWidthClass[props.width ?? InputWidthVariant.AUTO],
    props.taggable ? 'taggable-select' : '',
  ]);

  const inputWrapperClasses = computed(() => [
    inputWrapperClass,
    selectInputWrapperClass,
  ]);

  const optionsWithInitialValue = ref<
    string[] | number[] | Record<string, unknown>[]
  >(props.options);

  // if the initial value is not in the options, add it to the options (2 cases for string and object)
  watchEffect(() => {
    if (
      props.modelValue &&
      typeof props.modelValue === 'string' &&
      !(props.options as string[]).includes(props.modelValue)
    ) {
      optionsWithInitialValue.value = [
        ...props.options,
        props.modelValue,
      ] as string[];
      return;
    }
    if (
      props.options &&
      props.modelValue &&
      isObject(props.modelValue) &&
      props.options.find(
        (option: string | number | Record<string, unknown>) =>
          (option as Record<string, unknown>)[props.trackBy || 'value'] ===
          props.modelValue,
      )
    ) {
      optionsWithInitialValue.value = [
        ...props.options,
        props.modelValue,
      ] as Record<string, unknown>[];
      return;
    }
    optionsWithInitialValue.value = props.options;
  });

  // Validate existing tags when component mounts or validation regex changes
  watchEffect(() => {
    if (
      props.taggable &&
      Array.isArray(props.modelValue) &&
      props.tagValidationRegex
    ) {
      invalidTags.value.clear();
      for (const item of props.modelValue) {
        const tag = getTagKey(item);
        if (!validateTag(tag)) {
          invalidTags.value.add(tag);
        }
      }
    }
  });

  onMounted(() => {
    if (props.optionLabel && props.modelValue) {
      const match = optionsWithInitialValue.value?.find(
        (option: string | number | Record<string, unknown>) =>
          (option as Record<string, unknown>).value === props.modelValue,
      );

      if (match) {
        emit('update:modelValue', match as SelectModelValue);
      }
    }
  });
</script>

<template>
  <div :class="selectClasses">
    <slot name="inputLabel">
      <div :class="inputHeaderClass" v-if="label">
        <label v-if="label" :for="id" :class="inputLabelClass">{{
          label
        }}</label>
        <slot name="extraLabel"></slot>
      </div>
    </slot>
    <div :class="inputWrapperClasses">
      <div :class="rowCenterClass">
        <VueMultiselect
          :block-keys="['Delete']"
          ref="selectedElRef"
          :open-direction="openDirection"
          @tag="handleTag"
          @update:model-value="handleChange"
          @blur="handleBlur"
          @select="handleSelect"
          @search-change="handleSearch"
          :model-value="modelValue"
          :options="optionsWithInitialValue"
          :class="elClass"
          :use-teleport="useTeleport"
          :id="id"
          :data-test-id="testId"
          :name="name"
          :label="optionLabel"
          :loading="loading"
          :allow-empty="allowEmpty"
          :searchable="searchable"
          :close-on-select="closeOnSelect"
          :clear-on-select="clearOnSelect"
          :custom-label="customLabel"
          :hide-selected="hideSelected"
          :show-labels="showLabels"
          :disabled="disabled"
          :track-by="trackBy"
          :meta="meta"
          :taggable="taggable"
          :tag-position="tagPosition"
          :max-height="maxHeight"
          :max="max"
          :options-limit="optionsLimit"
          :limit="limit"
          :select-group-label="selectGroupLabel"
          :selected-label="selectedLabel"
          :deselect-label="deselectLabel"
          :deselect-group-label="deselectGroupLabel"
          :group-label="groupLabel"
          :group-select="groupSelect"
          :group-values="groupValues"
          :preselect-first="preselectFirst"
          :show-no-options="showNoOptions"
          :show-no-results="showNoResults"
          :show-pointer="showPointer"
          :tabindex="tabindex"
          :option-height="optionHeight"
          :multiple="multiple"
          :internal-search="internalSearch"
          :placeholder="placeholder"
          :tag-placeholder="tagPlaceholder || t('select.tagPlaceholder')"
        >
          <template #tag="{ option, remove }">
            <span
              :class="[
                'multiselect__tag',
                invalidTags.has(getTagKey(option))
                  ? 'multiselect__tag--invalid'
                  : '',
              ]"
            >
              <span>{{ getTagKey(option) }}</span>
              <i
                aria-hidden="true"
                @keypress.enter.prevent="remove(option)"
                @mousedown.prevent="remove(option)"
                class="multiselect__tag-icon"
              ></i>
            </span>
          </template>
          <template
            v-for="(_, slot) in $slots"
            v-slot:[slot]="scope"
            :key="slot"
            ><slot :name="slot" v-bind="scope" /></template
          >
          <template #caret="{ toggle }"
            ><FontIcon
              :icon-name="TUNDR_ICONS.CHEVRON_DOWN"
              :class="[customCaretClass, 'custom-caret']"
              @mousedown.prevent="toggle"
            /></template
          >
          <template #noOptions>
            <span> {{ noOptionsMessage || t('select.noOptions') }} </span>
          </template>
          <template #noResult>
            <span> {{ noResultsMessage || t('select.noResults') }} </span>
          </template>
        </VueMultiselect>
        <FontIcon
          v-if="resettable"
          clickable
          :disabled="!modelValue"
          :icon-name="TUNDR_ICONS.CLOSE"
          @click="handleReset"
        />
      </div>
      <slot name="inputError">
        <div v-if="showError && error" :class="errorClass">{{ error }}</div>
      </slot>
    </div>
  </div>
</template>
